<script setup lang="ts">
import { computed } from 'vue'

import {
  DATE_RANGE_OPTIONS,
  RANGE_LIMITS,
  useExportDateRange,
} from '../../composables/useExportDateRange'
import type { ExportDateOptionId, ExportDateRangeSelection } from '../../models/export.types'

import { useI18n } from '@/shared/i18n'
import { Input } from '@/shared/ui/input'
import { Select } from '@/shared/ui/select'
import type { SelectOption } from '@/shared/ui/select'

const props = defineProps<{ now?: Date }>()
const model = defineModel<ExportDateRangeSelection>({ required: true })
const { t } = useI18n()

const options = computed<SelectOption<ExportDateOptionId>[]>(() =>
  DATE_RANGE_OPTIONS.map((option) => ({ value: option.id, label: t(option.labelKey) })),
)
const option = computed({
  get: () => model.value.option,
  set: (value: ExportDateOptionId | null) => {
    if (value) model.value = { ...model.value, option: value }
  },
})
const amount = computed<string | number>({
  get: () => model.value.amount ?? 1,
  set: (value) => {
    const amountValue = Number(value)
    model.value = { ...model.value, amount: Number.isFinite(amountValue) ? amountValue : 1 }
  },
})
const customStart = computed<string | number>({
  get: () => model.value.customStart ?? '',
  set: (value) => {
    model.value = { ...model.value, customStart: String(value) }
  },
})
const customEnd = computed<string | number>({
  get: () => model.value.customEnd ?? '',
  set: (value) => {
    model.value = { ...model.value, customEnd: String(value) }
  },
})
const amountLimit = computed(() =>
  option.value === 'last_x_weeks' ? RANGE_LIMITS.weeks : RANGE_LIMITS.days,
)
const requiresAmount = computed(
  () => option.value === 'last_x_days' || option.value === 'last_x_weeks',
)
const derived = computed(() => useExportDateRange(model.value, props.now ?? new Date()))

defineExpose({ derived })
</script>

<template>
  <section class="export-date-range grid gap-md" :aria-label="t('dataExport.dateRange.title')">
    <Select
      v-model="option"
      :options="options"
      :label="t('dataExport.dateRange.optionLabel')"
      :placeholder="t('dataExport.dateRange.optionPlaceholder')"
      :empty-label="t('dataExport.common.noResults')"
    />

    <Input
      v-if="requiresAmount"
      v-model="amount"
      type="number"
      :min="1"
      :max="amountLimit"
      :label="t('dataExport.dateRange.amountLabel')"
    />

    <div v-if="option === 'custom'" class="export-date-range__custom grid grid-cols-2 gap-sm">
      <Input
        v-model="customStart"
        :label="t('dataExport.dateRange.startLabel')"
        :placeholder="t('dataExport.dateRange.datePlaceholder')"
      />
      <Input
        v-model="customEnd"
        :label="t('dataExport.dateRange.endLabel')"
        :placeholder="t('dataExport.dateRange.datePlaceholder')"
      />
    </div>

    <output class="export-date-range__derived text-subtle text-sm">
      {{ t('dataExport.dateRange.derived', { start: derived.firstDate, end: derived.lastDate }) }}
    </output>
  </section>
</template>
